// Lógica de apresentação da tela de SKU–Categoria, isolada do componente.
//
// Existe porque esta tela já produziu seis defeitos que build e lint não pegam,
// e todos eram "dado do backend entra, texto errado sai". Fora do componente
// isso vira função pura e ganha teste; dentro, só um navegador pega.
//
// A distinção que mais causou erro, e que todo mundo aqui tem de respeitar:
//
//   categoria_ouran   -> o que o CATÁLOGO diz. Cru. Não muda com ajuste.
//   categoria_padrao  -> o catálogo JÁ CORRIGIDO por uma correcao_padrao.
//   categoria_efetiva -> o padrão JÁ SUBSTITUÍDO por um vinculo_local.

export const ROTULOS_DE_ORIGEM = {
  branch_override: "Ajuste",
  client_override: "Ajuste",
  branch_standard: "Categoria padrão",
  client_standard: "Categoria padrão",
  branch_unclassified: "Sem categoria",
  client_unclassified: "Sem categoria",
  ouran_default: "Categoria padrão",
  catalogo: "Categoria padrão",
  pending: "Pendente",
  pendente: "Pendente",
};

export const rotuloDaOrigem = (origem) =>
  ROTULOS_DE_ORIGEM[origem] || origem || "—";

export const nivelDaOrigem = (origem) =>
  origem?.startsWith("branch_")
    ? "Nível: filial"
    : origem?.startsWith("client_")
      ? "Nível: cliente"
      : null;

/** Cor da etiqueta de origem.
 *
 * Antes olhava só `ajuste_ativo`, então um SKU com apenas correção de padrão
 * saía azul — a cor de "nada foi ajustado" — com o texto "Ajuste".
 */
export const corDaOrigem = (pilot) => {
  const origem = pilot?.categoria_efetiva_origem;
  if (origem === "pending" || origem === "pendente") return "orange";
  if (pilot?.ajuste_ativo) return "purple";
  if (pilot?.correcao_ativa) return "gold";
  return "blue";
};

/** Linha base da timeline: o que o CATÁLOGO diz, nunca o padrão já corrigido.
 *
 * Usar `categoria_padrao` aqui fazia a linha rotulada "catálogo Ouran" exibir
 * a categoria recém-corrigida, afirmando que o catálogo sempre disse aquilo.
 * E quando o catálogo não conhece o EAN a linha precisa aparecer dizendo isso:
 * é a informação que justifica ter corrigido.
 */
export const linhaBaseDaTimeline = (pilot, ajustes, ouranModeStart = null) => {
  if (!pilot) return null;
  const doCatalogo = pilot.categoria_ouran || null;
  const primeiro = (ajustes || []).reduce(
    (menor, linha) =>
      !menor || new Date(linha.start) < new Date(menor) ? linha.start : menor,
    null
  );
  return {
    id: "baseline",
    baseline: true,
    category_name: doCatalogo,
    // O catálogo não valeu "desde sempre": esta é a vigência operacional do
    // modo Ouran da filial, devolvida pelo backend a partir do histórico.
    start: ouranModeStart,
    end: primeiro,
    created_by: "—",
    reason: doCatalogo
      ? "Categoria padrão do catálogo Ouran"
      : "O catálogo Ouran não tem categoria para este EAN",
  };
};

/** Categoria que o formulário de ajuste deve pré-selecionar.
 *
 * A ordem importa: vínculo ativo primeiro, depois correção, e só então o
 * catálogo. Antes a correção era pulada, então um SKU com padrão corrigido
 * abria o formulário sugerindo a categoria crua do catálogo — justamente a que
 * alguém já tinha decidido estar errada.
 */
export const categoriaPrePreenchida = (pilot) =>
  pilot?.ajuste_ativo?.category_id ||
  pilot?.correcao_ativa?.category_id ||
  pilot?.categoria_id ||
  null;

export const resumoImpactoOuran = (preview) =>
  `${preview?.affected_skus || 0} SKU(s) efetivos em ${
    preview?.affected_branches || 0
  } filial(is); ${preview?.dormant_skus || 0} SKU(s) preparados em ${
    preview?.dormant_branch_count || 0
  } filial(is) legadas`;

/** O ajuste por cliente é ligado pelo EAN, e o backend tira esse EAN do
 * cadastro. A tela precisa olhar o mesmo campo, senão oferece o que o
 * salvamento vai recusar — ou esconde o que ele aceitaria.
 */
export const escopoClienteDisponivel = (pilot, podeAjustarCliente) => {
  if (!pilot?.gtin14) {
    return {
      habilitado: false,
      motivo:
        "Este SKU não tem EAN válido no cadastro, e o ajuste por cliente é ligado pelo EAN.",
    };
  }
  if (!podeAjustarCliente) {
    return { habilitado: false, motivo: "Requer administrador deste cliente." };
  }
  return { habilitado: true, motivo: null };
};

/** Um vínculo temporário que já resolve para "standard" não está desviando
 * nada — é o registro residual de um "voltar ao padrão" anterior. Só conta
 * como camada ativa quando ele muda a categoria efetiva de verdade.
 */
export const vinculoEstaDesviando = (pilot) =>
  Boolean(pilot?.ajuste_ativo) && pilot.ajuste_ativo.resolution !== "standard";

/** "Voltar à categoria padrão" existe para desfazer uma camada por vez:
 * primeiro o vínculo temporário, se ele estiver desviando; só quando ele não
 * está (nunca existiu, ou um clique anterior já o esvaziou) é que o próximo
 * clique alcança a correção de padrão.
 *
 * Sem essa distinção, um vínculo em resolution=standard continua existindo
 * como registro e trava todo clique seguinte na mesma camada — o SKU nunca
 * chega a desfazer a correção.
 */
export const camadaAReverter = (pilot) =>
  vinculoEstaDesviando(pilot) ? "vinculo_local" : "correcao_padrao";

/** Há algo para "voltar ao padrão" desfazer: um vínculo desviando, ou uma
 * correção de padrão ativa. Controla se o botão fica habilitado. */
export const temCamadaParaReverter = (pilot) =>
  vinculoEstaDesviando(pilot) || Boolean(pilot?.correcao_ativa);
